import React from 'react';
import styled, { css } from 'styled-components';
import { objectBrowserStoreSelector, useObjectBrowserStore } from '@warehouse/object-browser/domain';
import { isProxyableMediaFileName, NxObjectType, ProxyPlayback } from '@warehouse/object-browser/core';
import { ErrorAlert } from '@warehouse/shared/ui';
import { Alert, Button, NxLoader } from '@warehouse/shared/legacy-ui';
import { useProxyPlayback } from '../domain/useProxyPlayback';
import { RegenerateProxyStatus, useRegenerateProxy } from '../domain/useRegenerateProxy';
import { ShakaPlayer } from './ShakaPlayer';

export function ProxyPlayerSection() {
	const selectedNxObject = useObjectBrowserStore(objectBrowserStoreSelector.selectedNxObject);
	const selectedNxObjectFull = useObjectBrowserStore(objectBrowserStoreSelector.selectedNxObjectFull);

	const isPlayableMediaFile =
		selectedNxObject?.itemType === NxObjectType.File && isProxyableMediaFileName(selectedNxObject.name);
	const playback = useProxyPlayback();

	if (!isPlayableMediaFile) return null;

	// The proxy comes with the full object, so its eTag is the one that says whether the source changed.
	const eTag =
		selectedNxObjectFull?.nxObject.itemType === NxObjectType.File
			? selectedNxObjectFull.nxObject.eTag
			: selectedNxObject.eTag;

	return (
		<SectionWrapper aria-label="Proxy Player Section">
			<ProxyPlaybackView playback={playback} nxObjectUuid={selectedNxObject.uuid} eTag={eTag} />
		</SectionWrapper>
	);
}

function ProxyPlaybackView({
	playback,
	nxObjectUuid,
	eTag,
}: {
	playback: ProxyPlayback | undefined;
	nxObjectUuid: string;
	eTag: string;
}) {
	if (!playback) {
		return (
			<PlaceholderWrapper>
				<NxLoader size={24} />
			</PlaceholderWrapper>
		);
	}

	switch (playback.status) {
		case 'ready':
			return (
				<>
					{playback.outdated && <StaleProxyAlert nxObjectUuid={nxObjectUuid} />}
					<ShakaPlayer key={nxObjectUuid} nxObjectUuid={nxObjectUuid} eTag={eTag} playback={playback} />
				</>
			);
		case 'pending':
			return (
				<PlaceholderWrapper>
					<NxLoader size={24} />
					<PlaceholderText>Preview is being generated…</PlaceholderText>
				</PlaceholderWrapper>
			);
		case 'errored':
			return (
				<ErrorAlert
					errorMessage={
						playback.reason ? `Preview generation failed: ${playback.reason}` : 'Preview generation failed.'
					}
				/>
			);
		case 'notAvailable':
			return (
				<PlaceholderWrapper>
					<PlaceholderText>No preview available for this object.</PlaceholderText>
				</PlaceholderWrapper>
			);
		default:
			return null;
	}
}

const REGENERATE_BUTTON_LABELS: Record<RegenerateProxyStatus, string> = {
	idle: 'Regenerate proxy',
	submitting: 'Regenerating…',
	submitted: 'Regeneration started',
	failed: 'Retry regenerate',
	jobFailed: 'Retry regenerate',
};

const STALE_PROXY_MESSAGES: Record<RegenerateProxyStatus, string> = {
	idle: 'Stale proxy — this preview was made from an older version of this object.',
	submitting: 'Stale proxy — this preview was made from an older version of this object.',
	submitted: 'Stale proxy — this preview was made from an older version of this object.',
	failed: 'Could not start Mezz Proxy. Try again, or trigger it from the Logs tab.',
	jobFailed: 'Mezz Proxy did not finish regenerating this preview. Try again, or check the Logs tab.',
};

function StaleProxyAlert({ nxObjectUuid }: { nxObjectUuid: string }) {
	const { status, regenerate } = useRegenerateProxy({ nxObjectUuid });

	return (
		<Alert
			aria-label="Proxy Outdated"
			severity="warning"
			style={{ margin: 0 }}
			action={
				<Button
					type="button"
					nxstyle="secondary-blue"
					aria-label="Regenerate Proxy"
					disabled={status === 'submitting' || status === 'submitted'}
					onClick={regenerate}
				>
					{REGENERATE_BUTTON_LABELS[status]}
				</Button>
			}
		>
			{STALE_PROXY_MESSAGES[status]}
		</Alert>
	);
}

const SectionWrapper = styled.div(
	({ theme }) => css`
		display: flex;
		flex-direction: column;
		gap: ${theme.spacing(1)};
		margin-bottom: ${theme.spacing(2)};
	`,
);

const PlaceholderWrapper = styled.div(
	({ theme }) => css`
		align-items: center;
		background-color: ${theme.palette.light.background};
		border: 1px solid ${theme.palette.light.backgroundAlt};
		border-radius: 8px;
		display: flex;
		gap: ${theme.spacing(1)};
		justify-content: center;
		padding: ${theme.spacing(3)};
	`,
);

const PlaceholderText = styled.span(
	({ theme }) => css`
		color: ${theme.palette.text.secondary};
		font-size: ${theme.typography.bodyMediumRegular.fontSize};
		font-weight: ${theme.typography.bodyMediumRegular.fontWeight};
	`,
);
